
import React, { useEffect, useState, useMemo } from 'react';
import { Button, Card, CardBody, Typography, Chip } from '@material-tailwind/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ClockLoader } from 'react-spinners';
import { useUserContext } from '../../context';
import { useApi } from '../../hooks/useApi';
import { format, startOfDay, endOfDay, addDays, subDays } from 'date-fns';

export default function Notifications() {
    const { fetchAccessToken, defaultHotel } = useUserContext();
    const { getNotifications } = useApi();

    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedType, setSelectedType] = useState('All');

    const fetchNotifications = async (date) => {
        if (!defaultHotel) return;

        setIsLoading(true);
        try {
            const token = await fetchAccessToken();
            const startTime = startOfDay(date).getTime();
            const endTime = endOfDay(date).getTime();

            const response = await getNotifications(defaultHotel, startTime, endTime, token);

            if (Array.isArray(response)) {
                const sortedNotifications = response.sort((a, b) => b.createdOn - a.createdOn);
                setNotifications(sortedNotifications);
            } else {
                setNotifications([]);
            }
        } catch (error) {
            alert('Failed to fetch notifications');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (defaultHotel) {
            fetchNotifications(selectedDate);
        }
    }, [defaultHotel, selectedDate]);

    const handlePreviousDay = () => {
        const previousDay = subDays(selectedDate, 1);
        setSelectedDate(previousDay);
    };

    const handleNextDay = () => {
        const nextDay = addDays(selectedDate, 1);
        setSelectedDate(nextDay);
    };

    const filteredNotifications = useMemo(() => {
        if (selectedType === 'All') {
            return notifications;
        }

        return notifications.filter(notification => {
            let type = notification.notificationType || 'General';

            if (type === 'Advanced booking (Update)' || type === 'Advanced booking') {
                type = 'AB';
            }

            return type === selectedType;
        });
    }, [notifications, selectedType]);

    const formatTimestamp = (epochTime) => {
        return format(new Date(epochTime), 'HH:mm');
    };

    const formatNotificationBody = (body) => {
        if (!body) return 'No content available';

        const lines = body.split('\n');

        return lines.map((line, lineIndex) => {
            const parts = line.split('*');
            const formattedLine = parts.map((part, partIndex) => {
                if (partIndex % 2 === 1) {
                    return <strong key={partIndex}>{part}</strong>;
                }
                return part;
            });

            return (
                <div key={lineIndex}>
                    {formattedLine}
                    {lineIndex < lines.length - 1 && <br />}
                </div>
            );
        });
    };

    const getNotificationTypeColor = (type) => {
        const colorMap = {
            'Checkin': 'bg-blue-500',
            'Payment': 'bg-green-500',
            'Checkout': 'bg-orange-500',
            'Expense': 'bg-red-500',
            'Attendance': 'bg-purple-500',
            'Transfer': 'bg-indigo-500',
            'Convert': 'bg-yellow-500',
            'AB': 'bg-pink-500',
            'Advanced booking': 'bg-pink-500',
            'Advanced booking (Update)': 'bg-pink-500',
            'Payment delete': 'bg-gray-500',
            'General': 'bg-gray-400'
        };

        return colorMap[type] || 'bg-gray-400';
    };

    const getTypeChips = useMemo(() => {
        const fixedTypes = [
            "Checkin", "Payment", "Checkout", "Expense", "Attendance",
            "Transfer", "Convert", "AB", "Payment delete"
        ];

        const typeCounts = notifications.reduce((acc, notification) => {
            let type = notification.notificationType || 'General';

            if (type === 'Advanced booking (Update)' || type === 'Advanced booking') {
                type = 'AB';
            }

            acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});

        const totalCount = notifications.length;

        return (
            <div className="w-full overflow-x-auto mb-6">
                <div className="flex gap-2 min-w-max pb-2">
                    <Chip
                        value={`All (${totalCount})`}
                        variant={selectedType === 'All' ? 'filled' : 'outlined'}
                        color={selectedType === 'All' ? 'blue' : 'blue-gray'}
                        className="cursor-pointer whitespace-nowrap flex-shrink-0"
                        onClick={() => setSelectedType('All')}
                    />
                    {fixedTypes.map(type => {
                        const count = typeCounts[type] || 0;
                        return (
                            <Chip
                                key={type}
                                value={`${type}  (${count})`}
                                variant={selectedType === type ? 'filled' : 'outlined'}
                                color={selectedType === type ? 'blue' : 'blue-gray'}
                                className="cursor-pointer whitespace-nowrap flex-shrink-0"
                                onClick={() => setSelectedType(type)}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }, [notifications, selectedType]);

    return (
        <div className="mx-auto max-w-7xl px-2 py-2">
            <div className="mb-2">
                <div className="flex items-center justify-between mb-4">
                    <Typography variant="h4" className="mb-2">
                        Notifications
                    </Typography>
                    <a href="https://t.me/innproin_bot" target="_blank" className="text-blue-600 underline hover:text-blue-800">Telegram</a>
                </div>
                <div className="flex items-center gap-4 mb-4">
                    <Button
                        variant="outlined"
                        size="sm"
                        className="flex items-center gap-2"
                        onClick={handlePreviousDay}
                    >
                        <ChevronLeftIcon className="h-4 w-4" />

                    </Button>

                    <Typography variant="h6" className="mx-4">
                        {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                    </Typography>

                    <Button
                        variant="outlined"
                        size="sm"
                        className="flex items-center gap-2"
                        onClick={handleNextDay}
                    >
                        <ChevronRightIcon className="h-4 w-4" />
                    </Button>
                </div>

                {getTypeChips}
            </div>

            {isLoading && (
                <div className="flex justify-center items-center py-12">
                    <ClockLoader color="#2563eb" size={40} />
                </div>
            )}

            {!isLoading && (
                <div className="bg-gray-50 rounded-lg p-2 min-h-[500px] max-h-[600px] overflow-y-auto">
                    {filteredNotifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-2 text-center">
                            <div className="bg-white rounded-lg p-6 shadow-sm max-w-md">
                                <Typography color="gray">
                                    {selectedType === 'All'
                                        ? `No notifications for ${format(selectedDate, 'MMMM d, yyyy')}`
                                        : `No ${selectedType} notifications for ${format(selectedDate, 'MMMM d, yyyy')}`
                                    }
                                </Typography>
                            </div>
                        </div>
                    ) : (
                        filteredNotifications.map((notification, index) => (
                            <div key={index} className="flex justify-end mb-3">
                                <div className="max-w-[95%] md:max-w-[85%]">
                                    <div className="bg-white text-black border border-green-200 rounded-lg px-4 py-3 shadow-md relative">
                                        <div className="absolute top-0 right-0 w-0 h-0 border-l-[8px] border-l-green-200 border-t-[8px] border-t-transparent transform translate-x-[8px]"></div>

                                        <div className="text-sm leading-relaxed mb-2">
                                            {formatNotificationBody(notification.body)}
                                        </div>

                                        <div className="flex items-center justify-between gap-2 text-xs">
                                            <div className="flex items-center gap-2">
                                                {notification.notificationType && (
                                                    <span className={`${getNotificationTypeColor(notification.notificationType)} text-white px-2 py-1 rounded text-xs border`}>
                                                        {notification.notificationType}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1 text-gray-600">
                                                <span>{formatTimestamp(notification.createdOn)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}